// Temporary-use fee and short-term lease (临时用地使用费 / 短期租赁).
//
// Per rules/RULES_INDUSTRIAL.md §6:
//   临时用地使用费按临时使用年期对应的工业用途土地市场价格计收，
//   并适用土地使用年期修正系数。
//   政府或政府有关部门作为临时用地主体的，不计收临时用地使用费。
//
// Per rules/RULES_INDUSTRIAL.md §5 (other short-term leases):
//   适用土地使用年期修正系数。
//
// The implementation reuses the new-supply engine: the only
// correction applied is the year-term factor, the price is the
// industrial market price. Government / government-department
// users are exempt (per §6 last sentence).
//
// This module does NOT impose new factor rules. The year-term
// correction is already implemented by `calculateLandTermFactor`
// inside `calculateItem`.

import { MessageSink } from "./message-sink.js";

import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";
import type { ValidationMessage } from "../types/validation.js";

import { calculateNewSupplyLandPrice } from "./new-supply.js";
import type { CalculationResult } from "../types/calculation-result.js";

/**
 * Who is using the temporary land. Per RULES_INDUSTRIAL.md §6, a
 * government or government-department user is exempt.
 */
export type TemporaryUserType = "普通" | "政府或政府有关部门";

export interface TemporaryUseFeeInput {
  readonly parcel: Parcel;
  readonly prices: MarketPriceSet;
  readonly items: BuildingItem[];
  /** Lease horizon in years (also used for the year-term correction). */
  readonly landTermYears: number;
  readonly temporaryUserType: TemporaryUserType;
}

export interface TemporaryUseFeeResult {
  /** The underlying new-supply calculation result. For government users this is still computed so factor chains remain inspectable; the fee is clamped to 0. */
  readonly baseResult: CalculationResult;
  /** The payable temporary-use fee (0 for government users). */
  readonly totalFee: number;
  readonly validationMessages: ValidationMessage[];
  readonly note?: string;
}

const GOVERNMENT_NOTE =
  "按 RULES_INDUSTRIAL.md §6 末句：政府或政府有关部门作为临时用地主体的，不计收临时用地使用费。";

/**
 * Compute the temporary-use fee (or short-term lease price).
 *
 * Steps:
 *   1. Clone every input item with `landTermYears = input.landTermYears`
 *      so the year-term correction resolves to K(m) / K(n).
 *   2. Run the standard `calculateNewSupplyLandPrice`.
 *   3. If the user is government, return `totalFee = 0` + a note
 *      explaining the exemption. Otherwise return the engine's total.
 *
 * Note: the engine still applies the industry factors when the item
 * flags them, because they live inside `calculateItem` and the rules
 * don't say to override them for temporary use. If the user flags
 * `isIndustryOriented` / `industryProjectType` on a temporary-use
 * item, the existing exclusion rules (e.g. for 城市更新) still apply.
 */
export function calculateTemporaryUseFee(
  input: TemporaryUseFeeInput,
): TemporaryUseFeeResult {
  const leaseItems: BuildingItem[] = input.items.map((item) => ({
    ...item,
    landTermYears: input.landTermYears,
  }));

  const topSink = new MessageSink();
  const isGovernment = input.temporaryUserType === "政府或政府有关部门";
  if (isGovernment) {
    topSink.info("temporaryUserType", GOVERNMENT_NOTE);
  }

  const baseResult = calculateNewSupplyLandPrice({
    parcel: input.parcel,
    prices: input.prices,
    items: leaseItems,
  });

  const combinedMessages: ValidationMessage[] = [
    ...topSink.toArray(),
    ...baseResult.validationMessages,
  ];

  const totalFee = isGovernment ? 0 : baseResult.totalLandPrice;

  return {
    baseResult: {
      ...baseResult,
      validationMessages: combinedMessages,
    },
    totalFee,
    validationMessages: combinedMessages,
    note: isGovernment ? GOVERNMENT_NOTE : undefined,
  };
}